import {
  monthsOfWidth,
  parsePeriodLabel,
  periodEndDate,
} from "@/forwards2/periodUtils";
import { splitTakeOverPeriods } from "@/forwards2/picker/distributeTakes";

/**
 * One mandate's take beside its capacity per period of its cover of the range — the slices the take
 * will be split into, at the mandate's own width (`2027-Q1`, `2027-H1` or `2027`).
 */
export interface CoveredTake {
  take: number;
  capacity_by_period: Readonly<Record<string, number>>;
}

export interface PeriodVolume {
  label: string;
  mwh: number;
}

/**
 * The takes' estimated MWh per vintage: each take split over its own mandate's cover by
 * {@link splitTakeOverPeriods}, summed per period. Ordered by the delivery each period lands in,
 * narrower periods first, so `2027-Q2` and `2027-H1` sit together before `2027-Q3`.
 */
export function volumeByVintage(takes: readonly CoveredTake[]): PeriodVolume[] {
  const totals = new Map<string, number>();
  for (const { take, capacity_by_period } of takes) {
    for (const [period, volume] of Object.entries(
      splitTakeOverPeriods(take, capacity_by_period)
    )) {
      totals.set(period, (totals.get(period) ?? 0) + volume);
    }
  }
  return [...totals.entries()]
    .sort(([left], [right]) => compareByDelivery(left, right))
    .map(([label, mwh]) => ({ label, mwh }));
}

function compareByDelivery(left: string, right: string): number {
  const byEnd = (periodEndDate(left) ?? left).localeCompare(
    periodEndDate(right) ?? right
  );
  return byEnd !== 0 ? byEnd : widthMonths(left) - widthMonths(right);
}

function widthMonths(label: string): number {
  const parsed = parsePeriodLabel(label);
  return parsed ? monthsOfWidth(parsed.width) : 0;
}
